





























































































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { Route } from "vue-router";
import router from "@/router/router";
import { ClientArchivedProjectRow, ClientArchivedProjectSlice, ClientProjectRow } from "@/data/models/ClientAdministrationRow";
import {
  getClientArchivedProjectsForAdministration,
  getClientProjectsForAdministration,
} from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { setClientPageToolbarTitle } from "@/utils/ClientAdministrationTitle";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import MenuIconWithMultiSelectionListComponent from "@/components/common/MultiSelectionFilterMenu.vue";
import ClientProjectStepsComponent from "@/components/admin/clients/ClientProjectSteps.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import {
  clientProjectListLastVisitedKey,
  combineWithArchivedProjects,
  DEFAULT_PROJECT_LIST_SELECTION,
  isDefaultProjectListSelection,
  mergeArchivedProjectSlice,
  projectListSelectionFromQuery,
  selectionNeedsArchive,
  orderedProjectListSelection,
  PROJECT_LIST_OPTIONS,
  PROJECT_LIST_SORTS,
  ProjectKindOption,
  ProjectListOption,
  projectListOptionGroup,
  ProjectListSort,
  projectEndStateKey,
  ProjectStatusOption,
  selectProjectListRows,
} from "@/utils/ClientProjectList";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import { DeviceCountDisplay, getDeviceCountInfo } from "@/utils/ProjectDeviceCount";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";
import {
  getLastVisitedItem,
  LAST_VISITED_ITEM_BACKGROUND_STYLE,
  setLastVisitedItem,
  StorageKeyLastVisitedItemLevel,
} from "@/utils/Utils";

const ROW_SLICE = 100;

/** Page documents per archive call, as IRES Cloud reads them (`ARCHIVE_SLICE_PAGES`): for the progress bar. */
const ARCHIVE_SLICE_PAGES = 20;

/**
 * A client's projects in the admin portal, read-only, as IRES Admin's project screen lists them:
 * running and finished projects with their name, numbers, period, device count and step boxes, and
 * on demand the archive: ticking Alte or Gelöschte loads it in slices behind a progress bar, as IRES
 * Office's Alte Projekte does (decision was made on 2026-10-01 (Taimoor)). The default filter
 * (Aktuelle, Fertige) never loads it.
 *
 * Opened from the client detail's Projekte count. Filters (Status, Projektart), sort and search sit
 * in the app bar and the header names the active filters, as in the Geräte list. A row opens that
 * project's reports; the project itself cannot be opened from the portal client.
 *
 * **Kept alive** (`includeKeepAlive` in `Scaffold.vue`), so coming back from a project's reports
 * finds the same rows, filters and position instead of reading the overviews again; the project
 * opened last is tinted and scrolled back into view.
 *
 * **Not the access boundary**: the data comes from `getClientProjectsForAdministration`, which repeats
 * the portal and `MANAGE_CLIENTS` checks server-side and returns nothing personal (no contacts,
 * addresses or staff). Nothing here writes.
 */
@Component({
  name: "ClientAdministrationProjectsComponent",
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "multi-selection-menu-icon": MenuIconWithMultiSelectionListComponent,
    "client-project-steps": ClientProjectStepsComponent,
    "content-state": ContentStateComponent,
  },
})
export default class ClientAdministrationProjectsComponent extends Vue {
  readonly ROW_SLICE = ROW_SLICE;
  readonly emptyValue = EMPTY_VALUE;

  projects: ClientProjectRow[] = [];
  isLoading = true;
  loadError = false;
  /** The archive, once complete; empty until Alte or Gelöschte was asked for. */
  archivedProjects: ClientArchivedProjectRow[] = [];
  /** "idle" (not asked for), "loading", "loaded" or "failed" (retried by the next ask or refresh). */
  archiveState: "idle" | "loading" | "loaded" | "failed" = "idle";
  archiveLoadedPages = 0;
  /** null until the first slice says how many pages there are: the bar runs indeterminate. */
  archivePageCount: number | null = null;
  /** Bumped by every new archive load, so a superseded one (a refresh mid-load) stops. */
  archiveLoadGeneration = 0;
  searchQuery = "";
  /** The ticked filter options; the default (Aktuelle, Fertige) leaves the archive unloaded. */
  selection: ProjectListOption[] = [...DEFAULT_PROJECT_LIST_SELECTION];
  sort: ProjectListSort = ProjectListSort.NEWEST;
  rowLimit = ROW_SLICE;
  /** The last opened project's key, read once from session storage (Vue cannot track storage). */
  lastVisitedKey: string | null = null;
  /** Set when the user comes back from a project's reports, so the list scrolls to that project. */
  scrollToLastVisited = false;
  /** False while kept alive but not shown; gates the app bar portal. */
  componentActive = false;

  beforeRouteEnter(_to: Route, from: Route, next: (callback: (vm: Vue) => void) => void) {
    next((vm) => {
      if (from.name === ComponentName.CLIENT_ADMINISTRATION_REPORTS) {
        (vm as ClientAdministrationProjectsComponent).onBackFromReports();
      }
    });
  }

  get clientId(): string {
    const value = this.$route.query.clientId;
    return typeof value === "string" ? value : "";
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get rows(): ClientProjectRow[] {
    const all = combineWithArchivedProjects(this.projects, this.archiveState === "loaded" ? this.archivedProjects : []);
    return selectProjectListRows(all, this.selection, this.searchQuery, this.sort);
  }

  get defaultSelection(): ProjectListOption[] {
    return DEFAULT_PROJECT_LIST_SELECTION;
  }

  get isArchiveLoading(): boolean {
    return this.archiveState === "loading";
  }

  get archiveLoadPercentage(): number | null {
    if (this.archivePageCount === null || this.archivePageCount === 0) {
      return null;
    }
    return Math.min(100, Math.round((this.archiveLoadedPages / this.archivePageCount) * 100));
  }

  /** "40 % geladen", the wording of Alte Projekte, padded with FIGURE SPACE (one digit wide) so the bar does not shift. */
  get archiveLoadingText(): string {
    const percentage = String(this.archiveLoadPercentage).padStart(3, "\u2007");
    return this.$t("admin.clientProjects.archiveLoading", [percentage]).toString();
  }

  /** The column headings show only above rows. */
  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  get visibleRows(): ClientProjectRow[] {
    return this.rows.slice(0, this.rowLimit);
  }

  get filterOptions(): ProjectListOption[] {
    return PROJECT_LIST_OPTIONS.map((entry) => entry.option);
  }

  get sortOptions(): ProjectListSort[] {
    return PROJECT_LIST_SORTS;
  }

  /** The ticked options in menu order; nothing for the default (Aktuelle, Fertige). */
  get filterDescription(): string {
    if (isDefaultProjectListSelection(this.selection)) {
      return "";
    }

    return orderedProjectListSelection(this.selection).map((option) => this.optionName(option)).join(", ");
  }

  /** The archive is loaded the first time the filter can show it, not before. */
  @Watch("selection")
  onSelectionChanged() {
    if (selectionNeedsArchive(this.selection) && (this.archiveState === "idle" || this.archiveState === "failed")) {
      this.loadArchive();
    }
  }

  /** A changed filter, search or sort starts again at the first slice. */
  @Watch("selection")
  @Watch("searchQuery")
  @Watch("sort")
  onListChanged() {
    this.rowLimit = ROW_SLICE;
  }

  /** IRES Office's own project list wording. */
  sortName(sort: ProjectListSort): string {
    switch (sort) {
      case ProjectListSort.OLDEST:
        return this.$t("projectList.oldestProjects").toString();
      case ProjectListSort.PROJECT_NUMBER:
        return this.$t("clientData.projectNumberIres").toString();
      default:
        return this.$t("projectList.newestProjets").toString();
    }
  }

  optionKey(option: ProjectListOption): string {
    return option;
  }

  optionGroupName(option: ProjectListOption): string {
    return this.$t(`admin.clientProjects.filterGroups.${projectListOptionGroup(option)}`).toString();
  }

  /** Status as IRES Office's own project lists name it (Aktuelle / Fertige / Alte / Gelöschte); kinds as the type picker. */
  optionName(option: ProjectListOption): string {
    switch (option) {
      case ProjectStatusOption.RUNNING:
        return this.$t("projectList.types.running").toString();
      case ProjectStatusOption.FINISHED:
        return this.$t("projectList.types.finished").toString();
      case ProjectStatusOption.ARCHIVED:
        return this.$t("projectList.types.archived").toString();
      case ProjectStatusOption.DELETED:
        return this.$t("projectList.types.deleted").toString();
      case ProjectKindOption.FLEX:
        return this.$t("selectProjectType.flexProject").toString();
      case ProjectKindOption.LEAK_DETECTION:
        return this.$t("selectProjectType.leakDetection").toString();
      default:
        return this.$t("selectProjectType.drying").toString();
    }
  }

  kindLabel(project: ClientProjectRow): string {
    switch (project.kind) {
      case "flex":
        return this.optionName(ProjectKindOption.FLEX);
      case "leakDetection":
        return this.optionName(ProjectKindOption.LEAK_DETECTION);
      default:
        return this.optionName(ProjectKindOption.DRYING);
    }
  }

  /** The office number and the Sammelnummer under the IRES number, each named, when present. */
  secondaryNumbers(project: ClientProjectRow): string {
    const parts: string[] = [];

    if (project.projectNumber) {
      parts.push(this.$t("admin.clientProjects.officeNumber", [project.projectNumber]).toString());
    }
    if (project.projectNameErp) {
      parts.push(this.$t("admin.clientProjects.erpNumber", [project.projectNameErp]).toString());
    }

    return parts.join(" · ");
  }

  formatDate(millis: number | null): string {
    return millis === null ? EMPTY_VALUE : formatDateToDayMonthYear(new Date(millis)) ?? EMPTY_VALUE;
  }

  /** "seit 01.09.2026" while running, "01.09.2026 – 20.09.2026" once finished. */
  periodLabel(project: ClientProjectRow): string {
    if (project.startMillis === null) {
      return "–";
    }

    if (!project.finished) {
      return this.$t("admin.clientProjects.since", [this.formatDate(project.startMillis)]).toString();
    }

    return `${this.formatDate(project.startMillis)} – ${this.formatDate(project.endMillis)}`;
  }

  endStateLabel(project: ClientProjectRow): string {
    const key = projectEndStateKey(project);
    return key ? this.$t(key).toString() : "";
  }

  /** The three wordings of IRES Office's project rows; "–" for a project that never had a device. */
  deviceLabel(project: ClientProjectRow): string {
    const info = getDeviceCountInfo({
      numDevices: project.numDevices ?? undefined,
      numDevicesTotal: project.numDevicesTotal ?? undefined,
    });

    switch (info.display) {
      case DeviceCountDisplay.NONE_ACTIVE:
        return this.$tc("projectList.numAllDevicesNoneActive", info.totalDevices).toString();
      case DeviceCountDisplay.ALL_ACTIVE:
        return this.$tc("projectList.numAllDevicesAllActive", info.activeDevices).toString();
      case DeviceCountDisplay.SOME_ACTIVE:
        return this.$t("projectList.numAllDevices", [info.activeDevices, info.totalDevices]).toString();
      default:
        return "–";
    }
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(project: ClientProjectRow, hover: boolean): string {
    const hoverBackground = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";

    if (hoverBackground) {
      return `background-color: ${hoverBackground}`;
    }

    return clientProjectListLastVisitedKey(this.clientId, project.id) === this.lastVisitedKey
      ? LAST_VISITED_ITEM_BACKGROUND_STYLE
      : "";
  }

  onOpenReports(project: ClientProjectRow) {
    this.lastVisitedKey = clientProjectListLastVisitedKey(this.clientId, project.id);
    setLastVisitedItem(this.lastVisitedKey, StorageKeyLastVisitedItemLevel.LEVEL_2);

    router.push({
      name: ComponentName.CLIENT_ADMINISTRATION_REPORTS,
      query: { clientId: this.clientId, projectId: project.id },
    });
  }

  /** Scroll to the project just viewed once its row exists: now if it does, else after the load. */
  onBackFromReports() {
    this.scrollToLastVisited = true;

    if (!this.isLoading) {
      this.scrollToLastVisitedProject();
    }
  }

  /**
   * Bring the project whose reports were just viewed into view; deferred by one macrotask so it runs
   * after the router's own scroll to the top, as in the Mandanten list. A row beyond the rendered
   * slice is not scrolled to.
   */
  async scrollToLastVisitedProject() {
    if (!this.scrollToLastVisited || !this.lastVisitedKey) {
      return;
    }

    this.scrollToLastVisited = false;
    await this.$nextTick();
    await new Promise((resolve) => setTimeout(resolve, 0));

    const row = Array.from(this.$el.querySelectorAll<HTMLElement>("[data-project-id]")).find(
      (element) => clientProjectListLastVisitedKey(this.clientId, element.dataset.projectId ?? "") === this.lastVisitedKey
    );

    row?.scrollIntoView({ block: "center" });
  }

  onBack() {
    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_DETAIL, query: { clientId: this.clientId } });
  }

  async load() {
    this.isLoading = true;
    this.loadError = false;

    try {
      this.projects = this.clientId === "" ? [] : await getClientProjectsForAdministration(this.clientId);
    } catch {
      // Swallowed on purpose: the screen shows its own failure state (a refusal from Cloud
      // included), and the refresh button is the only useful action.
      this.loadError = true;
      this.projects = [];
    } finally {
      this.isLoading = false;
      this.rowLimit = ROW_SLICE;
    }

    this.scrollToLastVisitedProject();
  }

  /** The refresh button: the archive is reloaded too once it was asked for, so both halves are equally fresh. */
  refresh() {
    this.load();

    if (this.archiveState !== "idle") {
      this.loadArchive();
    }
  }

  /**
   * Loads the whole archive slice by slice, then hands it over at once: published per slice, rows
   * would land anywhere in the date-sorted list and the count would climb under the reader (IRES
   * Office's Alte Projekte works the same way). Only the progress bar moves until then.
   */
  async loadArchive() {
    if (this.clientId === "") {
      return;
    }

    const generation = ++this.archiveLoadGeneration;
    const isCurrent = () => generation === this.archiveLoadGeneration;

    this.archiveState = "loading";
    this.archiveLoadedPages = 0;
    this.archivePageCount = null;

    let archived: ClientArchivedProjectRow[] = [];
    let cursor: string | null = null;

    try {
      do {
        const slice: ClientArchivedProjectSlice = await getClientArchivedProjectsForAdministration(this.clientId, cursor);
        if (!isCurrent()) {
          return;
        }

        archived = mergeArchivedProjectSlice(archived, slice.projects);
        this.archivePageCount = slice.pageCount ?? this.archivePageCount;
        this.archiveLoadedPages += ARCHIVE_SLICE_PAGES;
        cursor = slice.nextCursor;
      } while (cursor !== null);

      this.archivedProjects = archived;
      this.archiveState = "loaded";
      this.rowLimit = ROW_SLICE;
    } catch {
      if (!isCurrent()) {
        return;
      }
      // The running and finished projects stay; the next ask for the archive, or a refresh, retries.
      this.archiveState = "failed";
      this.archivedProjects = [];
      SnackbarModule.showError({ key: "admin.clientProjects.archiveLoadFailed" });
    }
  }

  mounted() {
    this.componentActive = true;
    setClientPageToolbarTitle(this.clientId, this.$t("admin.clientProjects.title").toString());
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_2);
    // A preset from the detail (its Alte Projekte count), applied once; the selection watcher then
    // starts the archive. Later changes stay out of the URL.
    const preset = projectListSelectionFromQuery(this.$route.query as Record<string, unknown>);
    if (preset) {
      this.selection = preset;
    }
    this.load();
  }

  /** Back from the cache: the rows are still there, only the app bar and the tint need refreshing. */
  activated() {
    this.componentActive = true;
    setClientPageToolbarTitle(this.clientId, this.$t("admin.clientProjects.title").toString());
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_2);
  }

  deactivated() {
    this.componentActive = false;
  }
}
